import quarterMapUrl from "../assets/facet-quarter.webp";
import diamondBaseUrl from "../assets/phaser-hq/diamond-base.png";
import diamondUpgradedUrl from "../assets/phaser-hq/diamond-upgraded.png";
import developmentLayerUrl from "../assets/quarter-map-layers/development.png";
import friendsLayerUrl from "../assets/quarter-map-layers/friends.png";
import gamesLayerUrl from "../assets/quarter-map-layers/games.png";
import namePlaqueUrl from "../assets/quarter-map-layers/name-plaque.png";
import progressLayerUrl from "../assets/quarter-map-layers/progress.png";
import { PREMIUM_SHOP_ART } from "../config/artAssets";
import type { SectionId } from "../domain/types";
import type { Translator } from "../i18n/locales";
import { escapeHtml } from "./templates";

type LandmarkLayer = {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

type LandmarkPlaque = {
  x: number;
  y: number;
  angle: number;
};

type Landmark = {
  section: SectionId;
  className: string;
  ariaKey: Parameters<Translator>[0];
  titleKey: Parameters<Translator>[0];
  path: string;
  layer: LandmarkLayer;
  titleX?: number;
  titleY?: number;
  plaque?: LandmarkPlaque;
};

const PLAQUE_WIDTH = 232;
const PLAQUE_HEIGHT = 64.64;
const BUILDING_TITLE_SIZE = 24;
const MAP_DIAMOND = { x: 300, y: 355, width: 332, height: 292 } as const;

const LANDMARKS: readonly Landmark[] = [
  {
    section: "core",
    className: "quarter-hub",
    ariaKey: "map.home",
    titleKey: "map.diamond",
    path:
      "M300 438C304 391 351 361 466 355C581 361 628 391 632 438L604 486L466 647L328 486Z",
    layer: { url: diamondBaseUrl, ...MAP_DIAMOND },
    titleX: 466,
    titleY: 669,
  },
  {
    section: "development",
    className: "map-building map-building--development",
    ariaKey: "map.openDevelopment",
    titleKey: "map.development",
    path:
      "M20 1035L18 988L26 957L27 915L42 889L52 865L70 856L72 827L91 809L118 795L132 773L145 771L146 727L158 704L171 710L180 750L193 754L196 716L207 690L219 713L225 754L253 764L278 778L304 797L327 821L339 853L341 888L358 904L371 931L373 978L365 1013L348 1042L322 1054L304 1070L270 1082L231 1087L194 1082L165 1070L127 1077L91 1067L58 1055Z",
    layer: { url: developmentLayerUrl, x: 0, y: 650, width: 410, height: 470 },
    plaque: { x: 279, y: 1113, angle: -2.4 },
  },
  {
    section: "progress",
    className: "map-building map-building--progress",
    ariaKey: "map.openProgress",
    titleKey: "map.progress",
    path:
      "M565 1038L563 1008L575 980L573 934L585 906L585 850L600 827L613 820L613 781L626 751L638 776L642 811L656 800L663 746L679 724L690 681L710 651L732 637L744 620L750 582L761 562L771 591L774 627L796 641L813 666L822 704L839 716L846 746L870 758L874 792L890 807L889 851L906 868L910 918L925 947L922 1019L905 1050L880 1072L846 1088L807 1097L766 1089L727 1097L690 1090L655 1075L632 1086L600 1074L578 1058Z",
    layer: { url: progressLayerUrl, x: 520, y: 530, width: 421, height: 600 },
    plaque: { x: 696, y: 1124, angle: 4.1 },
  },
  {
    section: "friends",
    className: "map-building map-building--friends",
    ariaKey: "map.openFriends",
    titleKey: "map.friends",
    path:
      "M31 1448L28 1415L35 1360L48 1341L49 1270L59 1232L76 1218L104 1212L137 1180L169 1164L225 1166L258 1181L278 1180L305 1192L324 1214L356 1210L373 1232L378 1270L392 1290L393 1399L382 1430L354 1452L320 1461L288 1453L248 1466L205 1454L161 1460L114 1451L73 1460Z",
    layer: { url: friendsLayerUrl, x: 0, y: 1130, width: 430, height: 390 },
    plaque: { x: 281, y: 1498, angle: -3.2 },
  },
  {
    section: "games",
    className: "map-building map-building--games",
    ariaKey: "map.openGames",
    titleKey: "map.games",
    path:
      "M516 1434L507 1390L513 1325L524 1280L548 1238L582 1199L621 1176L658 1164L692 1163L724 1171L754 1166L803 1181L838 1203L866 1236L882 1274L893 1319L891 1383L876 1428L848 1460L809 1478L770 1489L726 1498L676 1490L629 1484L585 1465L548 1450Z",
    layer: { url: gamesLayerUrl, x: 480, y: 1060, width: 461, height: 470 },
    plaque: { x: 708, y: 1506, angle: 1.3 },
  },
] as const;

function renderPlaque(landmark: Landmark, t: Translator): string {
  if (landmark.plaque === undefined) {
    return `<text class="landmark-title landmark-title--hub" x="${landmark.titleX}" y="${landmark.titleY}" font-size="21" text-anchor="middle">${escapeHtml(t(landmark.titleKey))}</text>`;
  }

  const { x, y, angle } = landmark.plaque;
  return `
    <g class="landmark-label" transform="rotate(${angle} ${x} ${y})" aria-hidden="true">
      <g class="landmark-label-content">
        <image class="landmark-plaque" href="${escapeHtml(namePlaqueUrl)}" x="${x - PLAQUE_WIDTH / 2}" y="${y - PLAQUE_HEIGHT / 2}" width="${PLAQUE_WIDTH}" height="${PLAQUE_HEIGHT}" preserveAspectRatio="none"></image>
        <text class="landmark-title landmark-title--building" x="${x}" y="${y}" font-size="${BUILDING_TITLE_SIZE}" text-anchor="middle" dominant-baseline="central">${escapeHtml(t(landmark.titleKey))}</text>
      </g>
    </g>`;
}

function renderLandmark(landmark: Landmark, t: Translator): string {
  const object =
    landmark.section === "core"
      ? `<g class="map-diamond-float" aria-hidden="true">
          <g class="map-diamond-visible">${renderMapDiamondImages()}</g>
          <g class="landmark-object landmark-object--diamond">${renderMapDiamondImages()}</g>
        </g>`
      : `<image class="landmark-object" href="${escapeHtml(landmark.layer.url)}" x="${landmark.layer.x}" y="${landmark.layer.y}" width="${landmark.layer.width}" height="${landmark.layer.height}" preserveAspectRatio="none" aria-hidden="true"></image>`;

  return `
    <g class="landmark-entry">
      ${object}
      <a class="quarter-landmark ${landmark.className}" href="#${landmark.section}" tabindex="0" data-action="navigate" data-section="${landmark.section}" aria-label="${escapeHtml(
        t(landmark.ariaKey),
      )}">
        <path class="landmark-hit" d="${landmark.path}"></path>
        ${renderPlaque(landmark, t)}
        ${landmark.section === "progress" ? `<g class="map-reward-beacon-anchor" transform="translate(785 1098)" aria-hidden="true"><g class="map-reward-beacon"><circle r="29"></circle><circle r="21"></circle><circle class="map-reward-beacon__counter" cx="27" cy="-23" r="13"></circle><text x="0" y="2" text-anchor="middle" dominant-baseline="central">!</text><text class="map-reward-beacon__count" data-global-reward-count x="27" y="-23" text-anchor="middle" dominant-baseline="central">0</text></g></g>` : ""}
      </a>
    </g>`;
}

function renderMapDiamondImages(): string {
  return `<image class="map-diamond-image map-diamond-image--base" href="${escapeHtml(diamondBaseUrl)}" x="${MAP_DIAMOND.x}" y="${MAP_DIAMOND.y}" width="${MAP_DIAMOND.width}" height="${MAP_DIAMOND.height}" preserveAspectRatio="xMidYMid meet"></image>
    <image class="map-diamond-image map-diamond-image--upgraded" href="${escapeHtml(diamondUpgradedUrl)}" x="${MAP_DIAMOND.x}" y="${MAP_DIAMOND.y}" width="${MAP_DIAMOND.width}" height="${MAP_DIAMOND.height}" preserveAspectRatio="xMidYMid meet"></image>`;
}

export function renderQuarterMap(t: Translator): string {
  return `
    <div class="quarter-heading">
      <small>${escapeHtml(t("map.world"))}</small>
      <h1>${escapeHtml(t("map.title"))}</h1>
    </div>
    <button class="premium-gate premium-gate--map" type="button" data-action="open-premium-shop" aria-label="${escapeHtml(
      t("shop.open"),
    )}"><img src="${escapeHtml(PREMIUM_SHOP_ART.seasonSignet.url)}" alt="" width="${PREMIUM_SHOP_ART.seasonSignet.width}" height="${PREMIUM_SHOP_ART.seasonSignet.height}"/><span>${escapeHtml(
      t("shop.openShort"),
    )}</span></button>
    <div class="quarter-artboard">
      <img class="quarter-map-image" src="${escapeHtml(quarterMapUrl)}" alt="" width="941" height="1672" decoding="async" fetchpriority="high" data-visual-asset="quarter-map" aria-hidden="true">
      <svg class="quarter-map" viewBox="0 0 941 1672" role="group" aria-label="${escapeHtml(
        t("map.title"),
      )}" preserveAspectRatio="xMidYMid meet">
        ${LANDMARKS.map((landmark) => renderLandmark(landmark, t)).join("")}
      </svg>
    </div>`;
}
